import { useEffect, useRef } from 'react';
import localStorageUtil from '$/common/utils/localStorageUtil';
import { message } from '$/components/Message';
import useCreateRoom, { type AgentTeamRoomProgress } from '$/hooks/useCreateRoom';
import { useOfficialStore } from '$/store/useOfficialStore';
import { mtstatTrack } from '$/utils/mtstat';
import { getEnv } from '@/common/agents';
import { useCommonStore } from '@/store/useCommonStore';
import { navigateWithTeamStudioCoverAsync } from '@/pages/--roboneo/team_studio/utils/teamStudioRouteCover';
import { parsePopupAutofillScheme } from './parseScheme';

interface AutofillOperation {
  scheme: string;
  prompt: string;
  active: boolean;
  pending: boolean;
  handedOff: boolean;
  identity?: string;
  progress: AgentTeamRoomProgress;
}

/** 每个异步边界读取最新账号空间，避免旧活动任务进入另一个账号或团队。 */
function readIdentity() {
  const state = useOfficialStore.getState();
  return JSON.stringify([state.userInfo.uid, state.clientOId ?? 0, state.clientId, state.isOverseaServer]);
}

/** 失败仅释放本次房间的纯文本交接，不影响其他入口随后写入的数据。 */
function clearOwnHandoff(operation: AutofillOperation) {
  if (!operation.progress.roomId) return;
  try {
    const raw = localStorageUtil.getItem('popupData');
    const data = raw ? JSON.parse(raw) : null;
    if (data?.room_id === operation.progress.roomId && data.text === operation.prompt) {
      localStorageUtil.removeItem('popupData');
    }
  } catch { /* 清理失败不能掩盖原建房或导航错误。 */ }
}

/** 临时运营入口独立编排协议、登录和建房；删除本目录及 HomePopups 接线即可下线。 */
export function usePopupAutofill(onHandedOff: () => void) {
  const { createRoom } = useCreateRoom({ createAfterLogin: false });
  const mounted = useRef(false);
  const operationRef = useRef<AutofillOperation>();
  const onHandedOffRef = useRef(onHandedOff);
  onHandedOffRef.current = onHandedOff;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      const operation = operationRef.current;
      if (operation) {
        operation.active = false;
        if (!operation.handedOff) clearOwnHandoff(operation);
      }
    };
  }, []);

  /** ref 锁同步覆盖连点窗口，成功派发导航后继续锁定直到离开首页。 */
  function isBusy() {
    const operation = operationRef.current;
    return Boolean(operation?.pending || operation?.handedOff);
  }

  /** 正常关闭弹窗时丢弃失败进度，避免同链接的后续活动复用旧房间。 */
  function reset() {
    if (isBusy()) return;
    const operation = operationRef.current;
    if (operation) {
      operation.active = false;
      clearOwnHandoff(operation);
    }
    operationRef.current = undefined;
  }

  /** 显式等待登录后建房；已有房间/项目通过 completion 恢复，不修改公共 Hook。 */
  async function submit(context: AutofillOperation) {
    const operation = context;
    /** 登录阶段允许 UID 从空变为有效，操作自身必须始终有效。 */
    const isActive = () => mounted.current && operation.active && operationRef.current === operation;
    /** 创建阶段锁定账号与空间，包含 React 尚未重渲染的身份变化。 */
    const isCurrent = () => isActive() && operation.identity === readIdentity()
      && useOfficialStore.getState().isUserLoggedIn();
    let creating = false;
    try {
      if (!useOfficialStore.getState().isUserLoggedIn()) {
        try {
          await useCommonStore.getState().toLogin();
        } catch {
          return; // 取消登录只终止本次动作，不创建、不发送。
        }
        if (!isActive()) return;
        if (!useOfficialStore.getState().isUserLoggedIn()) return;
        await useCommonStore.getState().initUserInfo({ throwOnError: true });
      }
      if (!isActive()) return;
      if (!useOfficialStore.getState().isUserLoggedIn()) {
        message.error('RoboNeo_Web_login_status_fail');
        return;
      }
      operation.identity ??= readIdentity();
      if (!isCurrent()) return;
      creating = true;
      await createRoom({
        roomCreateParams: { extra: { entry_type: 'roboneo_team' } },
        prompt: operation.prompt,
        markElementList: [],
        isAutoSubmit: true,
        autoPrefixUnreferencedResources: false,
        trackLocation: 'home_page',
        trackType: 'agent_teams',
        isCurrent,
        completion: {
          /** 复用已确认成功的阶段，避免项目绑定或导航重试再次建房。 */
          getProgress: () => operation.progress,
          /** 迟到结果只记在所属操作，不污染当前弹窗的进度。 */
          onProgress: (progress) => { operation.progress = { ...progress }; },
          /** 导航 Promise 只表示派发完成，消息成功由工作区现有链路处理。 */
          navigate: async (target) => {
            if (!isCurrent() || !target.projectId || !target.canvasId) return false;
            await navigateWithTeamStudioCoverAsync({
              mode: 'url',
              url: getEnv().ENV === 'development' ? '/--roboneo/team_studio' : '/team_studio',
              query: { room_id: target.roomId, project_id: target.projectId, canvas_id: target.canvasId },
            }, isCurrent);
            if (!isCurrent()) return false;
            operation.handedOff = true;
            onHandedOffRef.current();
            // 统计失败不能撤销已派发导航，也不能清理目标页待消费的 prompt。
            try {
              mtstatTrack('command_send_click', {
                room_id: target.roomId,
                prompt_text: operation.prompt,
                message_type: 'prompt',
                image_num: 0,
                video_num: 0,
                file_num: 0,
                asset_num: 0,
                url: '',
                agent_id: '',
                ip_id_list: '',
                skill_id: '',
                content_from: '',
                input_model_name: 'auto,auto',
                input_model_value: 'auto,auto',
                mode: 'agent_teams',
                location: 'home_page',
              });
            } catch { /* 埋点不阻断业务。 */ }
            return true;
          },
        },
      });
    } catch {
      if (isActive() && !operation.handedOff) {
        message.error(creating ? 'RoboNeo_Web_chat_send_fail' : 'RoboNeo_Web_login_status_fail');
      }
    } finally {
      operation.pending = false;
      if (!operation.handedOff) clearOwnHandoff(operation);
    }
  }

  /** 同步决定是否接管，保证普通链接仍在原始用户手势中执行 window.open。 */
  function tryHandleScheme(scheme: string) {
    const parsed = parsePopupAutofillScheme(scheme);
    if (parsed.type === 'unhandled') return false;
    if (isBusy()) return true;
    if (parsed.type === 'invalid') {
      message.error('RoboNeo_Web_chat_send_fail');
      return true;
    }
    if (!mounted.current) return true;
    let operation = operationRef.current;
    if (!operation?.active || operation.scheme !== scheme
      || (operation.identity && operation.identity !== readIdentity())) {
      reset();
      operation = {
        scheme, prompt: parsed.prompt, active: true, pending: false, handedOff: false, progress: {},
      };
      operationRef.current = operation;
    }
    operation.pending = true;
    submit(operation);
    return true;
  }

  return { tryHandleScheme, isBusy, reset };
}
